import { useEffect, useMemo, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { useWizardStore } from '../../store/wizard.js';
import { GraphModeToggle, type GraphMode } from './GraphModeToggle.js';
import { colors } from '../../theme/tokens.js';
import { buildFamilyElements, familyClusterStylesheet } from './FamilyClusterView.js';
import { buildSpeciesElements, speciesStylesheet } from './SpeciesView.js';
import { log } from '../../lib/log.js';

interface RankableElement {
  data: { id?: unknown; rank?: unknown; source?: unknown; [key: string]: unknown };
}

interface SelectedNode {
  id: string;
  label: string;
  mode: GraphMode;
  rank?: number;
  count?: number;
  mass?: number;
  prob?: number;
  family?: string;
}

/**
 * Returns the id of the top-ranked node (rank 0) for the given mode, or null
 * for empty inputs / edge-only inputs. Used by the auto-pan effect to follow
 * the leading candidate as the candidate list shifts.
 */
export function computeTopNodeId(_mode: GraphMode, elements: RankableElement[]): string | null {
  for (const el of elements) {
    if ('source' in el.data) continue;
    if (typeof el.data.id === 'string' && el.data.rank === 0) {
      return el.data.id;
    }
  }
  return null;
}

export function formatPercent(value: number | undefined): string | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  if (value >= 0.1) return `${Math.round(value * 100)}%`;
  if (value >= 0.01) return `${(value * 100).toFixed(1)}%`;
  return '<1%';
}

export function selectedNodeFromData(mode: GraphMode, data: Record<string, unknown>): SelectedNode | null {
  if (typeof data.id !== 'string' || 'source' in data) return null;
  return {
    id: data.id,
    label: typeof data.label === 'string' ? data.label : data.id,
    mode,
    rank: typeof data.rank === 'number' ? data.rank : undefined,
    count: typeof data.count === 'number' ? data.count : undefined,
    mass: typeof data.mass === 'number' ? data.mass : undefined,
    prob: typeof data.prob === 'number' ? data.prob : undefined,
    family: typeof data.family === 'string' ? data.family : undefined,
  };
}

export function GraphPanel(): JSX.Element {
  const candidates = useWizardStore(s => s.candidates);
  const version = useWizardStore(s => s.version);
  const [mode, setMode] = useState<GraphMode>('family');
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  const candidateCount = candidates.length;
  const [hasAutoEngaged, setHasAutoEngaged] = useState(false);

  // When the candidate pool empties (post-reset), clear the auto-engage memory
  // so a fresh wizard run gets one new auto-engage opportunity.
  useEffect(() => {
    if (candidateCount === 0) setHasAutoEngaged(false);
  }, [candidateCount]);

  useEffect(() => {
    setSelectedNode(null);
  }, [mode, version]);

  // Auto-switch to species view when the candidate pool shrinks under 50.
  // Fires at most once per session — after the user picks a mode manually,
  // we never override their choice on subsequent updates.
  useEffect(() => {
    if (!hasAutoEngaged && mode === 'family' && candidateCount > 0 && candidateCount <= 50) {
      setMode('species');
      setHasAutoEngaged(true);
    }
  }, [candidateCount, mode, hasAutoEngaged]);

  const { elements, layout, stylesheet } = useMemo(() => {
    if (candidates.length === 0) return { elements: [], layout: { name: 'preset' }, stylesheet: cyStylesheet };
    if (mode === 'family') {
      const r = buildFamilyElements({ candidates });
      // Base sheet first, family sheet last so its node sizing/opacity wins.
      return { ...r, stylesheet: [...cyStylesheet, ...familyClusterStylesheet] };
    }
    const r = buildSpeciesElements({ candidates });
    return { ...r, stylesheet: [...cyStylesheet, ...speciesStylesheet] };
  }, [candidates, mode]);

  if (candidates.length === 0) {
    return <div className="flex h-full items-center justify-center text-bark-500">No candidates yet</div>;
  }

  return (
    <section className="relative flex h-full flex-col bg-paper-100">
      <header className="flex items-center justify-between border-b border-bark-300/40 px-4 py-2">
        <h2 className="font-display text-lg text-sage-900">Candidates</h2>
        <GraphModeToggle mode={mode} onChange={setMode} />
      </header>
      <div className="flex-1">
        <CytoscapeComponent
          // Cytoscape keeps existing nodes by id and can miss data-only
          // style updates for stable nodes. Version-remount so node sizes
          // reflect each candidate update immediately after a turn.
          key={`${mode}-${version}`}
          elements={elements}
          layout={layout}
          style={{ width: '100%', height: '100%' }}
          stylesheet={stylesheet}
          wheelSensitivity={0.2}
          cy={cy => {
            if (cy.scratch('_panHandlersAttached')) return;
            cy.scratch('_panHandlersAttached', true);
            cy.on('layoutstop', () => {
              if (!cy.scratch('_skippedFirstLayout')) {
                cy.scratch('_skippedFirstLayout', true);
                return; // skip the first layout — show the full field
              }
              const topId = computeTopNodeId(mode, cy.elements().jsons() as Array<{ data: { id?: unknown; rank?: unknown; source?: unknown } }>);
              if (!topId) return;
              const node = cy.getElementById(topId);
              if (node.nonempty()) {
                cy.animate({ center: { eles: node } }, { duration: 400, easing: 'ease-out' });
              }
            });
            cy.on('mouseover', 'node', e => e.target.addClass('is-hovered'));
            cy.on('mouseout', 'node', e => e.target.removeClass('is-hovered'));
            cy.on('tap', 'node', e => {
              log.debug('graph node clicked', { mode, id: e.target.id() });
              setSelectedNode(selectedNodeFromData(mode, e.target.data()));
            });
            cy.on('tap', e => {
              if (e.target === cy) setSelectedNode(null);
            });
          }}
        />
      </div>
      {selectedNode && (
        <aside className="absolute bottom-4 left-4 max-w-[min(22rem,calc(100%-2rem))] rounded-2xl border border-bark-300/50 bg-paper-50/95 p-4 text-sm text-ink-900 shadow-xl backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg leading-tight text-sage-900">{selectedNode.label}</h3>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-bark-500">
                {selectedNode.mode === 'family' ? 'Family cluster' : 'Species candidate'}
              </p>
            </div>
            <button
              type="button"
              className="rounded-full px-2 text-bark-500 transition hover:bg-bark-100 hover:text-ink-900"
              aria-label="Close node details"
              onClick={() => setSelectedNode(null)}
            >
              x
            </button>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            {formatPercent(selectedNode.mass) && (
              <div>
                <dt className="text-xs text-bark-500">Family likelihood</dt>
                <dd className="font-semibold text-sage-900">{formatPercent(selectedNode.mass)}</dd>
              </div>
            )}
            {formatPercent(selectedNode.prob) && (
              <div>
                <dt className="text-xs text-bark-500">Species likelihood</dt>
                <dd className="font-semibold text-sage-900">{formatPercent(selectedNode.prob)}</dd>
              </div>
            )}
            {selectedNode.count !== undefined && (
              <div>
                <dt className="text-xs text-bark-500">Active species</dt>
                <dd className="font-semibold text-sage-900">{selectedNode.count}</dd>
              </div>
            )}
            {selectedNode.family && (
              <div>
                <dt className="text-xs text-bark-500">Family</dt>
                <dd className="font-semibold text-sage-900">{selectedNode.family}</dd>
              </div>
            )}
            {selectedNode.rank !== undefined && (
              <div>
                <dt className="text-xs text-bark-500">Rank</dt>
                <dd className="font-semibold text-sage-900">#{selectedNode.rank + 1}</dd>
              </div>
            )}
          </dl>
        </aside>
      )}
    </section>
  );
}

const cyStylesheet = [
  {
    selector: 'node',
    style: {
      'background-color': colors.sage[500],
      'label': 'data(displayLabel)',
      'color': colors.ink[900],
      'font-family': 'Source Sans 3, system-ui, sans-serif',
      'font-size': 12,
      'text-valign': 'center',
      'text-halign': 'center',
      'border-color': colors.sage[700],
      'border-width': 1,
      'transition-property': 'opacity, background-color, width, height',
      'transition-duration': 400,
      'transition-timing-function': 'ease-out',
    },
  },
  {
    selector: 'node.is-hovered',
    style: {
      'label': 'data(label)',
      'z-index': 20,
      'border-width': 3,
      'border-color': colors.bloom[500],
      'text-background-color': colors.paper[50],
      'text-background-opacity': 0.92,
      'text-background-padding': 4,
      'text-border-color': colors.bark[300],
      'text-border-width': 0.5,
      'text-border-opacity': 0.6,
    },
  },
  {
    selector: 'edge',
    style: {
      'line-color': colors.bark[300],
      'width': 1,
      'opacity': 0.5,
    },
  },
];
